import { auth, googleProvider } from '~/firebaseInstance';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';

export interface OwnerProfile {
  displayName: string;
  email: string;
  photoURL: string;
}

// One listener for the whole app lifetime (SSR is off, so module scope is
// per-tab). Every initAuth() caller shares this promise: without it each call
// registered another onAuthStateChanged subscription that was never torn down,
// so every auth change fanned out to each leaked listener.
let authReadyPromise: Promise<void> | null = null;

export const useFirebaseAuth = () => {
  // const user = useState<User | null>('fb-user', () => null);
  const uid = useState<string | null>('fb-uid', () => null);
  const loading = useState<boolean>('fb-loading', () => true);
  // Shown on the Account / link-shop pages. Read straight from the Firebase
  // Auth user (already on the device) — no Firestore copy is kept.
  const profile = useState<OwnerProfile | null>('fb-profile', () => null);

  // Initialize auth state listener
  const initAuth = () => {
    if (authReadyPromise) return authReadyPromise;

    authReadyPromise = new Promise<void>((resolve) => {
      // Deliberately not unsubscribed: the listener keeps `uid` in sync for
      // later sign-in/sign-out events, not just the first resolution.
      onAuthStateChanged(auth, (firebaseUser) => {
        loading.value = false;
        if (!firebaseUser) {
          uid.value = null;
          profile.value = null;
          resolve();
          return
        }
        uid.value = firebaseUser.uid;
        profile.value = {
          displayName: firebaseUser.displayName || '',
          email: firebaseUser.providerData[0]?.email || firebaseUser.email || '',
          photoURL: firebaseUser.photoURL || '',
        };
        resolve();
      });
    });

    return authReadyPromise;
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result.user;
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  };

  return {
    uid: readonly(uid),
    loading: readonly(loading),
    profile: readonly(profile),
    initAuth,
    signInWithGoogle,
    signOut
  };
};
